import {
  createClient,
  type RealtimeChannel,
  type RealtimeChannelSendResponse,
  type SupabaseClient,
} from "@supabase/supabase-js";
import type {
  ActiveRoom,
  DailySnapshot,
  MultiplayerLeaderboard,
  RawActiveRoom,
  RawDailySnapshot,
  RawLeaderboard,
  RawRoomSnapshot,
  RoomSnapshot,
} from "../types";

function number(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function numbers(values: Array<number | string> | undefined): number[] {
  return (values ?? []).map(number);
}

function normalizeRoom(raw: RawRoomSnapshot): RoomSnapshot {
  return {
    roomId: raw.room_id,
    roomNumber: number(raw.room_number),
    membershipId: raw.membership_id,
    membershipStatus: raw.membership_status,
    alias: raw.alias,
    stateVersion: number(raw.state_version),
    serverNow: raw.server_now,
    phase: raw.phase,
    round: {
      id: raw.round.id,
      number: number(raw.round.number),
      pressedIndices: numbers(raw.round.pressed_indices),
      pressCount: number(raw.round.press_count),
      opensAt: raw.round.opens_at,
    },
    turn: {
      sequence: number(raw.turn.sequence),
      currentMembershipId: raw.turn.current_membership_id,
      startedAt: raw.turn.started_at,
      expiresAt: raw.turn.expires_at,
      isYours: raw.turn.is_yours,
    },
    queue: raw.queue.map((player) => ({
      membershipId: player.membership_id,
      alias: player.alias,
      wrongStreak: number(player.wrong_streak),
      dangerScore: number(player.danger_score),
      preselectedIndex: player.preselected_index === null ? null : number(player.preselected_index),
      isCurrent: player.is_current,
      isYou: player.is_you,
      colorIndex: number(player.color_index),
    })),
    lastFind: raw.last_find ? {
      roundId: raw.last_find.round_id,
      buttonIndex: number(raw.last_find.button_index),
      finderAlias: raw.last_find.finder_alias,
      remainingBefore: number(raw.last_find.remaining_before),
      clickCount: number(raw.last_find.click_count),
      endedStreak: number(raw.last_find.ended_streak),
      endedDangerScore: number(raw.last_find.ended_danger_score),
      foundAt: raw.last_find.found_at,
      pressedIndices: numbers(raw.last_find.pressed_indices),
    } : null,
    stats: {
      currentWrongStreak: number(raw.stats.current_wrong_streak),
      currentDangerScore: number(raw.stats.current_danger_score),
      bestWrongStreak: number(raw.stats.best_wrong_streak),
      bestDangerScore: number(raw.stats.best_danger_score),
      finds: number(raw.stats.finds),
      totalClicks: number(raw.stats.total_clicks),
      safeClicks: number(raw.stats.safe_clicks),
    },
    global: {
      totalClicks: number(raw.global.total_clicks),
      totalFinds: number(raw.global.total_finds),
    },
    events: (raw.events ?? []).map((event) => ({
      membershipId: event.membership_id,
      alias: event.alias,
      buttonIndex: number(event.button_index),
      isHidden: event.is_hidden,
      remainingBefore: number(event.remaining_before),
      wrongStreak: event.wrong_streak === undefined ? undefined : number(event.wrong_streak),
      dangerScore: event.danger_score === undefined ? undefined : number(event.danger_score),
      endedStreak: event.ended_streak === undefined ? undefined : number(event.ended_streak),
      endedDangerScore: event.ended_danger_score === undefined ? undefined : number(event.ended_danger_score),
    })),
  };
}

function normalizeLeaderboard(raw: RawLeaderboard): MultiplayerLeaderboard {
  return {
    leaders: raw.leaders.map((entry) => ({
      rank: number(entry.rank),
      alias: entry.alias,
      bestWrongStreak: number(entry.best_wrong_streak),
      finds: number(entry.finds),
      dangerScore: number(entry.danger_score),
      isYou: entry.is_you,
    })),
    myRank: raw.my_rank === null ? null : number(raw.my_rank),
  };
}

function normalizeActiveRoom(raw: RawActiveRoom): ActiveRoom {
  return {
    roomId: raw.room_id,
    roomNumber: number(raw.room_number),
    playerCount: number(raw.player_count),
    spectatorCount: number(raw.spectator_count),
    pressedCount: number(raw.pressed_count),
    lastActivityAt: raw.last_activity_at,
  };
}

function normalizeDaily(raw: RawDailySnapshot): DailySnapshot {
  return {
    runId: raw.run_id,
    playDate: raw.play_date,
    status: raw.status,
    serverNow: raw.server_now,
    expiresAt: raw.expires_at,
    pressedIndices: numbers(raw.pressed_indices),
    safeScore: number(raw.safe_score),
    dangerSurvived: number(raw.danger_survived),
    survivalChance: number(raw.survival_chance),
    hiddenIndex: raw.hidden_index === null ? null : number(raw.hidden_index),
    stats: {
      bestScore: number(raw.stats.best_score),
      completedDays: number(raw.stats.completed_days),
      perfectRuns: number(raw.stats.perfect_runs),
    },
    events: (raw.events ?? []).map((event) => ({
      buttonIndex: number(event.button_index),
      isHidden: event.is_hidden,
      score: number(event.score),
    })),
    discardedIndices: numbers(raw.discarded_indices),
  };
}

export class GameApi {
  private readonly client: SupabaseClient;

  public constructor(url: string, publishableKey: string) {
    this.client = createClient(url, publishableKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    });
  }

  public async initializeIdentity(captchaToken?: string): Promise<void> {
    const { data: { session }, error } = await this.client.auth.getSession();
    if (error) throw error;
    if (session) return;
    const result = await this.client.auth.signInAnonymously({
      options: captchaToken ? { captchaToken } : undefined,
    });
    if (result.error) throw result.error;
    if (!result.data.session) throw new Error("Supabase did not return an anonymous session.");
  }

  public async watchRoom(roomId: string | null = null): Promise<RoomSnapshot> {
    const { data, error } = await this.client.rpc("watch_button_room", { p_room_id: roomId });
    if (error) throw error;
    return normalizeRoom(data as RawRoomSnapshot);
  }

  public async joinGame(roomId: string): Promise<RoomSnapshot> {
    const { data, error } = await this.client.rpc("join_button_game", { p_room_id: roomId });
    if (error) throw error;
    return normalizeRoom(data as RawRoomSnapshot);
  }

  public async leaveGame(roomId: string): Promise<RoomSnapshot> {
    const { data, error } = await this.client.rpc("leave_button_game", { p_room_id: roomId });
    if (error) throw error;
    return normalizeRoom(data as RawRoomSnapshot);
  }

  public async listActiveRooms(): Promise<ActiveRoom[]> {
    const { data, error } = await this.client.rpc("list_active_button_rooms");
    if (error) throw error;
    return (data as RawActiveRoom[]).map(normalizeActiveRoom);
  }

  public async getRoom(roomId: string): Promise<RoomSnapshot> {
    const { data, error } = await this.client.rpc("get_button_room_state", { p_room_id: roomId });
    if (error) throw error;
    return normalizeRoom(data as RawRoomSnapshot);
  }

  public async setPreselection(roomId: string, roundId: string, index: number | null): Promise<RoomSnapshot> {
    const { data, error } = await this.client.rpc("set_button_preselection", {
      p_room_id: roomId,
      p_round_id: roundId,
      p_index: index,
    });
    if (error) throw error;
    return normalizeRoom(data as RawRoomSnapshot);
  }

  public async pressButton(room: RoomSnapshot, index: number): Promise<RoomSnapshot> {
    const { data, error } = await this.client.rpc("press_button", {
      p_room_id: room.roomId,
      p_round_id: room.round.id,
      p_turn_sequence: room.turn.sequence,
      p_index: index,
    });
    if (error) throw error;
    return normalizeRoom(data as RawRoomSnapshot);
  }

  public async expireTurn(roomId: string, turnSequence: number): Promise<RoomSnapshot> {
    const { data, error } = await this.client.rpc("expire_button_turn", {
      p_room_id: roomId,
      p_turn_sequence: turnSequence,
    });
    if (error) throw error;
    return normalizeRoom(data as RawRoomSnapshot);
  }

  public async heartbeat(roomId: string): Promise<boolean> {
    const { data, error } = await this.client.rpc("heartbeat_button_room", { p_room_id: roomId });
    if (error) throw error;
    return Boolean(data);
  }

  public async getLeaderboard(): Promise<MultiplayerLeaderboard> {
    const { data, error } = await this.client.rpc("get_multiplayer_leaderboard");
    if (error) throw error;
    return normalizeLeaderboard(data as RawLeaderboard);
  }

  public async getDaily(): Promise<DailySnapshot> {
    const { data, error } = await this.client.rpc("get_daily_solo");
    if (error) throw error;
    return normalizeDaily(data as RawDailySnapshot);
  }

  public async submitDaily(snapshot: DailySnapshot, indices: number[]): Promise<DailySnapshot> {
    const { data, error } = await this.client.rpc("submit_daily_solo_clicks", {
      p_run_id: snapshot.runId,
      p_play_date: snapshot.playDate,
      p_indices: indices,
    });
    if (error) throw error;
    return normalizeDaily(data as RawDailySnapshot);
  }

  public subscribeToRoom(
    roomId: string,
    onStateChanged: (stateVersion: number, kind: string) => void,
    onStatus: (status: string) => void,
  ): RealtimeChannel {
    const channel = this.client.channel(`button-room:${roomId}`, {
      config: { private: true, broadcast: { self: false, ack: false } },
    });
    channel
      .on("broadcast", { event: "state_changed" }, ({ payload }) => {
        const message = payload as { state_version?: unknown; kind?: unknown };
        onStateChanged(number(message.state_version), String(message.kind ?? "state_changed"));
      })
      .subscribe(onStatus);
    return channel;
  }

  public async unsubscribe(channel: RealtimeChannel | null): Promise<RealtimeChannelSendResponse | "ok"> {
    if (!channel) return "ok";
    return this.client.removeChannel(channel);
  }

  public async deleteMyData(): Promise<void> {
    const { error } = await this.client.rpc("delete_my_data");
    if (error) throw error;
    await this.client.auth.signOut({ scope: "local" });
  }
}
